import { Injectable } from '@angular/core';
import { ProcessPageTypeCodeEnum } from '@interfaces';
import { ISubmitResponse } from '@interfaces/models/ISubmitResponse';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { openChoiceEnabledFlowTypes } from '@shared/helpers/open-choice';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { selectOAFromStore } from '@shared/services/open-account/oa.selectors';
import { OpenAccountReviewService } from '@shared/services/open-account/review/oa-review.service';
import { OpenAccountSubmitService } from '@shared/services/open-account/submit/oa-submit.service';
import { TealeafTrackingService } from '@shared/services/tealeaf-tracking/tealeaf-tracking.service';
import { catchError, forkJoin, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { ClearService } from 'src/app/shared/services/clear/clear.service';
import { selectRecaptchaMockResult } from 'vg-interaction-model';

import { FMFService } from '../../../../shared/services/fraud-micro-frontend/fmf.service';
import { LogError, LogInfo, TrackProcessError } from '../../actions';
import {
  submitOpenAccount,
  submitOpenAccountComplete,
  submitOpenAccountFailure,
  submitOpenAccountResponse,
  validateOpenAccount,
  validateOpenAccountFailure,
  validateOpenAccountResponse,
} from '../../actions/open-account/open-account.actions';
import * as routerActions from '../../actions/router/router.actions';
import { openSpinner } from '../../actions/spinner/spinner.actions';
import { appianSubmit, cipSubmit } from '../../actions/submit/submit.actions';
import {
  submitMetricsOAEnd,
  submitMetricsOASuccess,
} from '../../actions/submit-metrics/submit-metrics.actions';
import * as fromRootStore from '../../index';
import { selectClearData } from '../../selectors/document-upload/document-upload.selectors';
import { selectFraudDetectorSimulatorLevel } from '../../selectors/fraud-detector-simulator/fraud-detector-simulator.selectors';
import { selectOpenAccountResponse } from '../../selectors/open-account/open-account.selectors';

@Injectable()
export class OpenAccountEffects {
  CONFIRM = 'confirm';
  SYSTEM_ERROR = 'error';

  constructor(
    private actions$: Actions,
    private openAccountReviewService: OpenAccountReviewService,
    private openAccountSubmitService: OpenAccountSubmitService,
    private tealeafTrackingService: TealeafTrackingService,
    private fmfService: FMFService,
    private clearService: ClearService,
    private store$: Store<fromRootStore.State>,
  ) {}

  validateOpenAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(validateOpenAccount),
      withLatestFrom(
        this.store$.pipe(select(selectOAFromStore)),
        this.store$.pipe(select(selectRecaptchaMockResult)),
        this.store$.pipe(select(fromRootStore.flowTypeSelection)),
      ),
      switchMap(([, openAccountData, mockRecaptchaLevel, flowType]) => {
        this.tealeafTrackingService
          .callTealeafTracking({
            countryOfResidence: openAccountData?.personalInfo[0]?.countryOfResidence,
            Mailing_Street: openAccountData?.contactInfo[0]?.mailingAddress?.street,
            Mailing_City: openAccountData?.contactInfo[0]?.mailingAddress?.city,
            Mailing_State: openAccountData?.contactInfo[0]?.mailingAddress?.state,
            Mailing_zipCode: openAccountData?.contactInfo[0]?.mailingAddress?.zipCode,
          })
          .subscribe();

        return forkJoin([
          of(openAccountData),
          this.fmfService.getAllTokens({ recaptcha: 'COO_OA_VALIDATE' }),
          of(mockRecaptchaLevel),
          of(flowType),
        ]);
      }),
      switchMap(([openAccountData, fmfResponse, mockRecaptchaLevel, flowType]) => {
        return this.openAccountReviewService
          .openAccountValidate(openAccountData, fmfResponse, mockRecaptchaLevel)
          .pipe(
            switchMap((validated: boolean) => {
              if (validated) {
                if (openChoiceEnabledFlowTypes.includes(flowType)) {
                  return [
                    validateOpenAccountResponse({ payload: validated }),
                    fromRootStore.CheckClientEligibility(),
                  ];
                } else {
                  return [
                    validateOpenAccountResponse({ payload: validated }),
                    routerActions.Next(),
                  ];
                }
              } else {
                return [
                  validateOpenAccountResponse({ payload: validated }),
                  routerActions.Go({ path: ['error'] }),
                  LogError({
                    payload: {
                      eventId: 'error-page-route-OA-validate-fail',
                      message: 'Unable to validate client input',
                      code: LoggerCodes.NULL_FAILURE,
                    },
                  }),
                ];
              }
            }),
            catchError(() => {
              return [
                validateOpenAccountFailure(),
                submitMetricsOAEnd({
                  payload: ProcessPageTypeCodeEnum.SourceOfFunds,
                }),
                routerActions.Go({ path: ['error'] }),
                LogError({
                  payload: {
                    eventId: 'error-page-route-OA-validate-error',
                    message: 'Error validating client input',
                    code: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ];
            }),
          );
      }),
    ),
  );

  submitOpenAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitOpenAccount),
      withLatestFrom(
        this.store$.pipe(select(selectOAFromStore)),
        this.store$.pipe(select(selectClearData)),
        this.store$.pipe(select(selectRecaptchaMockResult)),
        this.store$.pipe(select(selectFraudDetectorSimulatorLevel)),
      ),
      tap(() => this.store$.dispatch(openSpinner())),
      switchMap(([, openAccountData, clearData, mockRecaptchaLevel, mockFraudDetectorLevel]) => {
        return forkJoin([
          of(openAccountData),
          of(clearData),
          this.fmfService.getAllTokens({ recaptcha: 'COO_OA_SUBMIT' }),
          of(mockFraudDetectorLevel),
          of(mockRecaptchaLevel),
        ]);
      }),
      switchMap(
        ([openAccountData, clearData, fmfResponse, mockFraudDetectorLevel, mockRecaptchaLevel]) => {
          const openAccountPayload = { ...openAccountData };
          return this.openAccountSubmitService
            .openAccount(
              openAccountPayload,
              fmfResponse,
              mockFraudDetectorLevel,
              mockRecaptchaLevel,
            )
            .pipe(
              switchMap((submitResponse: ISubmitResponse) => {
                const actions = [];
                actions.push(
                  submitOpenAccountResponse({ payload: submitResponse }),
                  submitOpenAccountComplete(),
                );
                if (!submitResponse.success) {
                  actions.push(
                    submitOpenAccountFailure(),
                    submitMetricsOAEnd({
                      payload: ProcessPageTypeCodeEnum.Esign,
                    }),
                  );
                } else {
                  actions.push(submitMetricsOASuccess());
                }
                if (clearData.uploadedFilename) actions.push(cipSubmit());
                return actions;
              }),
              catchError((e) => {
                LogError({
                  payload: {
                    eventId: 'coo-oa_error',
                    message: `Open Account Error: ${e}`,
                  },
                });

                if (e.eventMessage) {
                  return [routerActions.Go({ path: ['error'] })];
                }
                return [
                  submitOpenAccountResponse({ payload: { success: false } as ISubmitResponse }),
                  submitOpenAccountComplete(),
                  submitOpenAccountFailure(),
                  submitMetricsOAEnd({ payload: ProcessPageTypeCodeEnum.Esign }),
                ];
              }),
            );
        },
      ),
    ),
  );

  submitOpenAccountError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitOpenAccountFailure),
      map(() => {
        return TrackProcessError({
          errorCode: 'coo-oa_error',
        });
      }),
    ),
  );

  appianSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitOpenAccountComplete),
      map(() => {
        return appianSubmit();
      }),
    ),
  );

  cipSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cipSubmit),
      withLatestFrom(
        this.store$.pipe(select(selectClearData)),
        this.store$.pipe(select(selectOAFromStore)),
        this.store$.pipe(select(selectOpenAccountResponse)),
      ),
      switchMap(([, clearData, oaData, oaResponse]) => {
        const cipBody = {
          uploadedFilename: clearData.uploadedFilename,
          ssn_private: oaData.personalInfo[0].ssn_private,
          s24Status: oaResponse.s24Status,
          confirmationNumber: oaResponse.confirmationNumber,
          sendToAWD: oaResponse.sendToAWD,
          accountNumber: oaResponse.brokerageAccountNumber,
          accountId: oaResponse.accountId,
        };

        return this.clearService.callCIPUpload(cipBody).pipe(
          switchMap(() => {
            return [
              LogInfo({
                payload: {
                  eventId: 'cipSubmit-effect-success',
                  message: 'CIP Appian case created, calling COO Appian',
                  code: LoggerCodes.NULL_FAILURE,
                },
              }),
              appianSubmit(),
            ];
          }),
        );
      }),
      catchError((error) => {
        return [
          LogError({
            payload: {
              eventId: 'cipSubmit-effect-error',
              message: 'CIP Appian case error, calling COO Appian',
              error: error,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
          appianSubmit(),
        ];
      }),
    ),
  );
}
